import React from 'react';
import { Redirect, Route } from 'react-router';
import { AccessError, Props } from 'routes/types';
import { UserNotLoggedError } from 'routes/errors';
import PageNotFound from 'pages/errorPages/404';
import NotPermittedPage from 'pages/errorPages/403';
import { ERRORS_ENUM, ROUTE_GROUPS_ENUM } from 'routes/enums';
import { mapAvailablePageToBackendPermittedPage } from 'utils/user/permissions';
import { getPage } from 'routes/pageProvider';
import { getPageMap } from 'routes/routeMapProvider';
import { decoratePageComponentWithPermission } from './permittedPageComponentDecorator';
import { login } from 'routes/routePaths';
import permissionService from 'routes/permissionService';
import { getLayoutFromComponent, LayoutProvider } from 'layouts/LayoutProvider';
import { useLocation } from 'react-router-dom';

const ProtectedRoute: React.FC<Props> = ({
  elseRoute = login,
  exact = false,
  routeGroup = ROUTE_GROUPS_ENUM.DEFAULT,
  pageName,
  user,
  isLogged,
  ...rest
}) => {
  const location = useLocation();
  if (pageName) {
    try {
      const permissionGroup = permissionService.createPermissionGroup(
        user,
        pageName,
        mapAvailablePageToBackendPermittedPage(routeGroup, pageName),
      );

      if (!isLogged) throw UserNotLoggedError();

      const page = getPage(getPageMap(routeGroup), permissionGroup);
      const pageWithPermission = page ? decoratePageComponentWithPermission(page, permissionGroup.permission) : page;

      return (
        <LayoutProvider layout={getLayoutFromComponent(page)}>
          <Route render={pageWithPermission} exact={exact} {...rest} />
        </LayoutProvider>
      );
    } catch (error) {
      const e: AccessError = error;

      if (e.code === ERRORS_ENUM.NOT_AUTHORISED) {
        return (
          <LayoutProvider>
            <Route component={NotPermittedPage} />
          </LayoutProvider>
        );
      }

      if (e.code === ERRORS_ENUM.NOT_FOUND || e.code === ERRORS_ENUM.NOT_MAPPED) {
        return (
          <LayoutProvider>
            <Route component={PageNotFound} />
          </LayoutProvider>
        );
      }

      return (
        <Redirect
          to={{
            pathname: elseRoute,
            state: {
              from: location.pathname,
            },
          }}
        />
      );
    }
  }

  return <Redirect to={elseRoute} />;
};

export default ProtectedRoute;
